import { useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";

import { AccountShell } from "../../components/desktop/account-nav.tsx";
import { Button, Empty, ErrorBlock, Img, OrderStatus, Price, Spinner } from "../../components/ui.tsx";
import { cn } from "@/lib/utils";
import { ORDER_FILTERS, RefundPanel } from "../orders.tsx";
import {
  ordersQueryFor,
  useCancelOrder,
  useConfirmReceipt,
  useMe,
  useOrders,
} from "../../api/queries.ts";

/**
 * ═══════════════════════════════════════════════════════════════
 *  网页版 · 我的订单
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **一单一块，商品在左、金额和动作固定在右。**
 *    手机版是"商品 → 金额条 → 一排按钮"竖着堆，按钮挤在一行里；
 *    网页上把金额和动作收进右边一条固定宽度的栏，**每一单的按钮都在同一条
 *    竖线上** —— 十几单排下来，眼睛不用横向找"确认收货"在哪。
 *
 * ⚠️ 页签排在最上面并且**落在 URL 上**：网页上用户会存书签、发链接，
 *    也会用浏览器后退在页签之间跳。
 */
export function DesktopOrders() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/orders" }) as { status?: string };
  const { data: me, isPending: mePending } = useMe();
  const { data, isPending, error, refetch } = useOrders(
    { ...ordersQueryFor(search.status), limit: 20 },
    Boolean(me),
  );
  const confirm = useConfirmReceipt();
  const cancel = useCancelOrder();
  const [refundFor, setRefundFor] = useState<number | null>(null);

  if (mePending)
    return (
      <AccountShell title="我的订单">
        <Spinner compact />
      </AccountShell>
    );
  if (!me)
    return (
      <AccountShell title="我的订单">
        <Empty title="登录后查看订单" action={{ label: "去登录", to: "/login" }} compact />
      </AccountShell>
    );

  return (
    <AccountShell title="我的订单">
      {/* ── 页签 ── */}
      <div className="mb-5 flex gap-1 border-b border-border">
        {ORDER_FILTERS.map((f) => {
          const active = (search.status ?? "") === f.key;
          return (
            <button
              key={f.key || "all"}
              onClick={() =>
                navigate({ to: "/orders", search: { status: f.key || undefined } as never })
              }
              className={cn(
                "relative px-4 py-2.5 text-[15px] transition-colors",
                active ? "font-medium text-foreground" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {f.label}
              {active && (
                <span className="absolute inset-x-3 bottom-0 h-0.5 rounded-full bg-brand-600" />
              )}
            </button>
          );
        })}
      </div>

      {isPending ? (
        <Spinner compact />
      ) : error ? (
        <ErrorBlock error={error} onRetry={() => refetch()} />
      ) : data.items.length === 0 ? (
        <Empty
          title={search.status ? "这个状态下还没有订单" : "还没有订单"}
          action={{ label: "去逛逛", to: "/products" }} compact />
      ) : (
        <div className="space-y-4">
          {data.items.map((o) => (
            <div key={o.id} className="overflow-hidden rounded-xl border border-border bg-card">
              {/* ── 单头：订单号 / 时间 / 状态 ── */}
              <div className="flex items-center gap-4 border-b border-border bg-secondary/40 px-5 py-2.5 text-[13px]">
                <span className="font-mono text-muted-foreground">{o.orderNo}</span>
                <span className="text-muted-foreground">
                  {new Date(o.createdAt).toLocaleString("zh-CN", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
                <span className="ml-auto">
                  <OrderStatus payment={o.payment} fulfillment={o.fulfillment} />
                </span>
              </div>

              <div className="grid grid-cols-[minmax(0,1fr)_200px]">
                {/* ── 左：商品 ── */}
                <div className="divide-y divide-border">
                  {o.items.map((it) => (
                    <div key={it.id} className="flex items-center gap-3 px-5 py-3.5">
                      {/*
                        ⚠️ 明细里的字段名带 `Snapshot` 后缀（下单当时的快照）——
                           写成 `title` / `specs` 会拿到 undefined。
                      */}
                      <Img
                        src={it.imageUrl}
                        alt={it.titleSnapshot}
                        className="size-14 shrink-0 rounded-lg object-cover"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="line-clamp-1 text-[14px]">{it.titleSnapshot}</p>
                        <p className="mt-0.5 text-[12px] text-muted-foreground">
                          {Object.values(it.specsSnapshot).join(" · ")}
                        </p>
                      </div>
                      <span className="shrink-0 text-right text-[13px] text-muted-foreground">
                        <Price cents={it.unitPrice} /> × {it.quantity}
                      </span>
                    </div>
                  ))}
                </div>

                {/* ── 右：金额与动作（每一单都在同一条竖线上） ── */}
                <div className="flex flex-col items-stretch gap-2 border-l border-border px-5 py-4">
                  <p className="text-center text-[13px] text-muted-foreground">实付</p>
                  <p className="mb-1 text-center">
                    <Price cents={o.payableAmount} className="text-[20px] text-brand-600" />
                  </p>

                  {o.payment === "pending" && o.fulfillment === "pending" && (
                    <>
                      {/*
                        ⚠️ 文案是「去付款」不是「去支付」：这个商城默认走线下收款，
                           点进去看到的是"把钱付给谁、怎么付"，不是收银台。
                      */}
                      <Link to="/pay" search={{ orderIds: String(o.id) } as never}>
                        <Button className="w-full">去付款</Button>
                      </Link>
                      <Button variant="outline" onClick={() => cancel.mutate(o.id)}>
                        取消订单
                      </Button>
                    </>
                  )}
                  {o.fulfillment === "shipped" && (
                    <Button onClick={() => confirm.mutate(o.id)} loading={confirm.isPending}>
                      确认收货
                    </Button>
                  )}
                  {/*
                    🔴 付了钱就能申请售后。建材售后率不低（磕碰、色差、尺寸不符），
                       结算特意压到"确认收货 + 15 天"就是为了留出这段维权期。
                  */}
                  {(o.payment === "paid" || o.payment === "partial_refunded") && (
                    <Button variant="outline" onClick={() => setRefundFor(o.id)}>
                      申请退款
                    </Button>
                  )}
                  {/* 🔴 物流单号只有详情页才有 —— 建材是大件配送，用户要跟司机对接。 */}
                  <Link
                    to="/orders/$id"
                    params={{ id: String(o.id) }}
                    className="mt-1 text-center text-[13px] text-muted-foreground transition-colors hover:text-foreground"
                  >
                    查看详情{o.fulfillment === "shipped" ? "与物流" : ""} →
                  </Link>
                </div>
              </div>

              {refundFor === o.id && (
                <div className="border-t border-border px-5 py-4">
                  <RefundPanel orderId={o.id} onClose={() => setRefundFor(null)} />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </AccountShell>
  );
}
